import {
  type AnnulOperation,
  type BranchChangeset,
  type ConfigurationOperation,
  isLargeOperation,
  isParameterDefinitionCreateOperation,
  isParameterValueAmendOperation,
  isParameterValueCreateOperation,
  type ParameterDefinitionCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { LayerKey, type Tree } from '@festina-life/api'
import {
  assertNotEmpty,
  dateUtils,
  notEmpty,
  type Optional,
} from '@festina-life/core'

import type { ConfigurationItemsByBlock } from '../../workspace/operationsContext'
import {
  findActiveAnnulments,
  indexAnnulmentOperations,
  indexChangesetOperations,
} from '../annulOperations/helpers'
import {
  ensureIndexedBlockLayerEntity,
  parameterDefinitionFindFn,
  userActionsForOperation,
} from '../helpers'
import { constructIndexedOperations } from '../indexedOperations/helpers'
import type { OperationWithSourceInfo, WorkspaceOperationOf } from '../types'
import type {
  IndexedParameterDefinitionCreateOperations,
  IndexedParameterDefinitionCreateOperationWithMetaData,
  IndexedParameterValueOperations,
  ParameterValueEntityOperation,
  ParameterValueOperation,
} from './types'

export const getParameterDefinitionAtDate = (
  indexedParameterDefinition: Optional<IndexedParameterDefinitionCreateOperations>,
  atDate: Optional<Date>,
) => {
  const operations = indexedParameterDefinition?.operations ?? []

  const comparableDate = atDate?.getTime()

  return (operations ?? []).findLast(
    (compareTo) =>
      !compareTo.internalOperation.entity?.fromDate ||
      (notEmpty(comparableDate) &&
        dateUtils
          .dateUtcAsLocal(compareTo.internalOperation.entity?.fromDate)
          .valueOf() <= comparableDate),
  )
}

export const getParameterDefinitionInOut = (
  from: IndexedParameterDefinitionCreateOperationWithMetaData,
) => {
  assertNotEmpty(from.internalOperation?.entity)

  return from.internalOperation?.entity
}

export const getValueDefinitionName = (operation: ParameterValueOperation) => {
  if (isLargeOperation(operation)) {
    assertNotEmpty(operation.innerOperationName)

    return operation.innerOperationName
  } else {
    assertNotEmpty(operation.entity?.name)

    return operation.entity.name
  }
}

/**
 * Whether the operation carries a parameter value entity, either creating a value or
 * amending an existing one, as opposed to a large operation whose payload has to be
 * fetched separately.
 */
export const isParameterValueEntityOperation = (
  operation: ConfigurationOperation,
): operation is ParameterValueEntityOperation =>
  isParameterValueCreateOperation(operation) ||
  isParameterValueAmendOperation(operation)

export const isParameterValueOperation = (
  operation: ConfigurationOperation,
): operation is ParameterValueOperation =>
  isParameterValueCreateOperation(operation) ||
  isParameterValueAmendOperation(operation) ||
  (isLargeOperation(operation) &&
    operation.innerOperationType === 'parameterValueCreate')

export const constructIndexedParameterValueOperations = ({
  configurationChangesets,
  draftValueOperations,
  draftAnnulOperations,
  rootBlock,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  draftValueOperations: Optional<
    WorkspaceOperationOf<ParameterValueEntityOperation>[]
  >
  draftAnnulOperations?: WorkspaceOperationOf<AnnulOperation>[]
  rootBlock: string
}) => {
  const indexedAnnulments = indexAnnulmentOperations({
    changesets: configurationChangesets ?? [],
    baseBlock: rootBlock,
  })
  const indexedOperations = indexChangesetOperations(
    configurationChangesets ?? [],
  )

  const valueOperationsWithLayerAndTenant: OperationWithSourceInfo<ParameterValueOperation>[] =
    (configurationChangesets ?? []).flatMap((branchChangeset) =>
      (branchChangeset.changeset?.operations ?? [])
        .filter(notEmpty)
        .filter(isParameterValueOperation)
        .map((operation) => {
          const operationWithoutAnnulments = {
            layer: new LayerKey({
              tenantId: branchChangeset.changeset?.tenantId,
              layerId: branchChangeset.changeset?.layer,
            }),
            block: branchChangeset.block ?? rootBlock,
            internalOperation: operation,
            isDraft: false,
            changesetHistory: branchChangeset.changesetHistory,
            actions: userActionsForOperation(
              operation,
              branchChangeset.changesetHistory,
            ),
          }

          const annulOperations = findActiveAnnulments({
            operation: operationWithoutAnnulments,
            changesetAnnulments: indexedAnnulments,
            operationIndexes: indexedOperations,
            workspaceAnnulments: draftAnnulOperations ?? [],
          })

          return {
            ...operationWithoutAnnulments,
            annulOperations,
          }
        }),
    )

  const draftOperationsWithLayerAndTenant: OperationWithSourceInfo<ParameterValueEntityOperation>[] =
    draftValueOperations?.map((draftOperation) => ({
      isDraft: true,
      layer: new LayerKey({
        tenantId: draftOperation.tenantId,
        layerId: draftOperation.layer,
      }),
      block: draftOperation.block ?? rootBlock,
      internalOperation: draftOperation.operation,
      changesetHistory: undefined,
      actions: [],
      annulOperations: [],
    })) ?? []

  const allResults: ConfigurationItemsByBlock<IndexedParameterValueOperations> =
    new Map()

  const pushToResult = (
    operation: OperationWithSourceInfo<ParameterValueOperation>,
  ) => {
    const { internalOperation, layer } = operation
    const parameterId = getValueDefinitionName(internalOperation)
    assertNotEmpty(parameterId)

    const indexedParameterValue = ensureIndexedBlockLayerEntity({
      allResults,
      block: operation.block,
      layer,
      entityId: parameterId,
      createDefault: () => [],
    })

    // No sorting is considered for parameter value operations. We simply append them in the order they are processed.
    // Sorting is only relevant for the flattened list of operations, and handled within the flattenValueOperations function.
    indexedParameterValue.push(operation)
  }

  valueOperationsWithLayerAndTenant.forEach((operation) =>
    pushToResult(operation),
  )
  draftOperationsWithLayerAndTenant.forEach((draftOperation) => {
    pushToResult(draftOperation)
  })

  return allResults
}

export const isParameterDefinitionActiveAtDate =
  (viewAt: Date) =>
  (value: Optional<IndexedParameterDefinitionCreateOperations>) =>
    !!value?.operations.some((operation) => {
      const fromDate = operation.internalOperation.entity?.fromDate

      return (
        !fromDate ||
        dateUtils.dateUtcAsLocal(fromDate).valueOf() <= viewAt.getTime()
      )
    })

export const constructIndexedParameterDefinitionCreateOperations = ({
  configurationChangesets,
  draftCreateOperations,
  draftAnnulOperations,
  blockTopology,
}: {
  configurationChangesets: Optional<BranchChangeset[]>
  draftCreateOperations: Optional<
    WorkspaceOperationOf<ParameterDefinitionCreateOperation>[]
  >
  draftAnnulOperations?: WorkspaceOperationOf<AnnulOperation>[]
  blockTopology: Tree
}): ConfigurationItemsByBlock<IndexedParameterDefinitionCreateOperations> =>
  constructIndexedOperations<
    ParameterDefinitionCreateOperation,
    IndexedParameterDefinitionCreateOperations
  >({
    configurationChangesets,
    draftOperations: draftCreateOperations,
    draftAnnulOperations,
    blockTopology,
    isIndexedOperation: isParameterDefinitionCreateOperation,
    getEntityId: (createOperation) => createOperation.entity?.name,
    createDefault: (parameterId) => ({
      parameterId,
      operations: [],
      // These fields are updated once all operations have been added.
      lastParameterEntity: undefined,
      packageName: undefined,
      isDraft: false,
    }),
    findIndexFn: parameterDefinitionFindFn,
    postProcessEntity: (indexedParameterDefinition) => {
      const lastParameterDefinition =
        indexedParameterDefinition.operations.at(-1)

      indexedParameterDefinition.lastParameterEntity =
        lastParameterDefinition?.internalOperation.entity
      indexedParameterDefinition.packageName =
        lastParameterDefinition?.internalOperation.entity?.packageName
    },
  })
